// Why @mantine/core's Modal instead of design-system's Modal:
// design-system's Modal is fixed at 343px wide and built around three
// sub-components (Heading, Content, Actions) optimized for short, single-purpose
// dialogs. The document upload modal is a multi-step flow (5 views) with file
// lists, previews, and dropzones; 343px is too narrow. We continue to use
// @mantine/core's Modal directly with package-defined sizing in the SCSS module.
import { useEffect, useState } from "react";
import { Modal } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  IconDefinition,
} from "@fortawesome/sharp-regular-svg-icons";
import { faArrowLeft } from "@fortawesome/sharp-light-svg-icons";
import { getPageCountForFile } from "../../utils/page-count";
import { ChooseDocumentTypeView } from "./views/choose-document-type-view";
import { UploadDocumentView } from "./views/upload-document-view";
import { QrCodeUploadSection } from "./views/qr-code-upload-section";
import { ViewFileView } from "./views/view-file-view";
import { DeleteConfirmationView } from "./views/delete-confirmation-view";
import { CloseConfirmationView } from "./views/close-confirmation-view";
import {
  AppealSupportingDocumentFileType,
  AppealSupportingDocumentType,
  DocumentUploadModalMode,
  DocumentUploadModalNavigationDirection,
  DocumentUploadModalProps,
  DocumentUploadModalView,
  ModalViewState,
} from "./types";

import styles from "./document-upload-modal.module.scss";

const PDF_MIME_TYPE = "application/pdf";

const HARDCODED_MODE_DOCUMENT_TYPES: Partial<
  Record<DocumentUploadModalMode, AppealSupportingDocumentType>
> = {
  [DocumentUploadModalMode.ADD_LOMN]:
    AppealSupportingDocumentType.LetterOfMedicalNecessity,
  [DocumentUploadModalMode.ADD_NOBC]:
    AppealSupportingDocumentType.NoticeOfBenefitChange,
  [DocumentUploadModalMode.ADD_DENIAL_LETTER]:
    AppealSupportingDocumentType.DenialLetter,
};

type HeadingIconProps = {
  className: string;
};

const HeadingIcon = ({ className }: HeadingIconProps) => (
  <div className={className}>
    <FontAwesomeIcon icon={faFileLines} />
  </div>
);

type BackToUploadViewButtonProps = {
  onClick: () => void;
  className: string;
};

const BackToUploadViewButton = ({
  onClick,
  className,
}: BackToUploadViewButtonProps) => (
  <button
    onClick={onClick}
    type="button"
    aria-label="Go back to your uploaded files"
    className={className}
  >
    <FontAwesomeIcon icon={faArrowLeft as IconDefinition} />
  </button>
);

type TitleProps = {
  currentView: DocumentUploadModalView;
  onNavigate: () => void;
};

const Title = ({ currentView, onNavigate }: TitleProps) => {
  if (currentView === DocumentUploadModalView.VIEW_FILE) {
    return (
      <BackToUploadViewButton
        onClick={onNavigate}
        className={styles.backToUploadViewButton}
      />
    );
  }

  return <HeadingIcon className={styles.headingIcon} />;
};

export const DocumentUploadModal = ({
  isOpen,
  onClose,
  onSubmitDocument,
  mode,
  document,
  onLoadingDocumentFilesError,
  appealId,
  config,
  deps,
  slots,
  qrUpload,
}: DocumentUploadModalProps) => {
  const { Button, UploadFileInput } = slots;
  const { additionalModes, excludedDocumentTypes, documentTypeDescriptions } =
    config;

  const [currentView, setCurrentView] = useState<ModalViewState>({
    view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
  });
  const [selectedDocumentType, setSelectedDocumentType] =
    useState<AppealSupportingDocumentType | null>(null);
  const [otherDocumentName, setOtherDocumentName] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [saveDocumentError, setSaveDocumentError] = useState<string | null>(
    null,
  );
  const [isDocumentUpsertLoading, setIsDocumentUpsertLoading] = useState(false);
  const [viewFileUrls, setViewFileUrls] = useState<string[] | null>(null);
  const [isGettingViewFileUrls, setIsGettingViewFileUrls] = useState(false);
  const [hasErrorGettingViewFileUrls, setHasErrorGettingViewFileUrls] =
    useState(false);
  const [isLoadingDocumentFiles, setIsLoadingDocumentFiles] = useState(false);
  const [initialFiles, setInitialFiles] = useState<File[]>([]);

  useEffect(() => {
    let currentUrls: string[] | null = null;

    const handleLoadingViewFileUrls = async () => {
      if (currentView.view === DocumentUploadModalView.VIEW_FILE) {
        setIsGettingViewFileUrls(true);
        setHasErrorGettingViewFileUrls(false);

        try {
          const fileUrls = await deps.getFileUrlsForFile(currentView.file);

          currentUrls = fileUrls;
          setViewFileUrls(fileUrls);
        } catch (error) {
          /* oxlint-disable-next-line no-console */
          console.error(
            `Error getting file urls to view document file ${error}`,
          );
          setHasErrorGettingViewFileUrls(true);
        } finally {
          setIsGettingViewFileUrls(false);
        }
      }
    };

    handleLoadingViewFileUrls();

    return () => {
      if (currentUrls) {
        currentUrls.forEach((url) => URL.revokeObjectURL(url));
      }
    };
  }, [currentView, deps]);

  useEffect(() => {
    const additionalModeEntry = additionalModes?.find(
      (entry) => entry.mode === mode,
    );

    if (mode === DocumentUploadModalMode.ADD) {
      setCurrentView({
        view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
      });
      return;
    }

    const hardcodedDocumentType = HARDCODED_MODE_DOCUMENT_TYPES[mode];

    if (hardcodedDocumentType) {
      setCurrentView({
        view: DocumentUploadModalView.UPLOAD_DOCUMENT,
        documentType: hardcodedDocumentType,
        otherDocumentName: null,
      });
      setSelectedDocumentType(hardcodedDocumentType);
      return;
    }

    if (additionalModeEntry) {
      setCurrentView({
        view: DocumentUploadModalView.UPLOAD_DOCUMENT,
        documentType: additionalModeEntry.documentType,
        otherDocumentName: null,
      });
      setSelectedDocumentType(additionalModeEntry.documentType);
      return;
    }

    if (!document) {
      throw new Error(
        "Document was not provided when setting document upload modal to edit mode",
      );
    }

    const loadDocumentFiles = async () => {
      setIsLoadingDocumentFiles(true);

      try {
        const downloadedDocumentFiles = await Promise.all(
          document.files.map((documentFile) =>
            deps.downloadFile(documentFile.url),
          ),
        );

        setUploadedFiles(downloadedDocumentFiles);
        setInitialFiles(downloadedDocumentFiles);
      } catch (error) {
        /* oxlint-disable-next-line no-console */
        console.error("Error loading document files:", error);
        onLoadingDocumentFilesError();
      } finally {
        setIsLoadingDocumentFiles(false);
      }
    };

    setSelectedDocumentType(document.documentType);
    setCurrentView({
      view: DocumentUploadModalView.UPLOAD_DOCUMENT,
      documentType: document.documentType,
      otherDocumentName: document.otherDocumentTypeName ?? null,
    });
    loadDocumentFiles();
  }, [
    isOpen,
    mode,
    document,
    onLoadingDocumentFilesError,
    additionalModes,
    deps,
  ]);

  const hasUnsavedChanges =
    (initialFiles.length && initialFiles !== uploadedFiles) ||
    (!initialFiles.length && uploadedFiles.length);

  const closeModal = () => {
    onClose();
    setCurrentView({ view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE });
    setSelectedDocumentType(null);
    setOtherDocumentName("");
    setUploadedFiles([]);
    setSaveDocumentError(null);
    setViewFileUrls(null);
    setIsGettingViewFileUrls(false);
    setHasErrorGettingViewFileUrls(false);
    setInitialFiles([]);
  };

  const handleOnClose = () => {
    if (hasUnsavedChanges) {
      setCurrentView({
        view: DocumentUploadModalView.CLOSE_CONFIRMATION,
        navigationDirection: DocumentUploadModalNavigationDirection.CLOSE,
      });
    } else {
      closeModal();
    }
  };

  const generateUploadUrlsAndUploadFiles = async (
    filesToUpload: File[],
    filesWithPageCounts: Array<{
      fileName: string;
      fileType: AppealSupportingDocumentFileType;
      pageCount: number;
    }>,
    documentType: AppealSupportingDocumentType,
    otherDocumentTypeName: string | undefined,
  ) => {
    const generateData = await deps.generateUploadUrls({
      documentType,
      otherDocumentTypeName,
      files: filesWithPageCounts,
    });

    if (
      !generateData ||
      generateData.fileUploadDetails.length !== filesToUpload.length
    ) {
      throw new Error("Failed to generate upload URLs");
    }

    await Promise.all(
      filesToUpload.map((file, index) =>
        deps.uploadFileToUrl(
          file,
          generateData.fileUploadDetails[index].uploadUrl,
        ),
      ),
    );

    return generateData;
  };

  const handleSaveDocument = async () => {
    setSaveDocumentError(null);

    if (!selectedDocumentType) {
      setCurrentView({ view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE });
      return;
    }

    try {
      setIsDocumentUpsertLoading(true);

      const otherDocumentTypeName =
        selectedDocumentType === AppealSupportingDocumentType.Other
          ? otherDocumentName
          : undefined;

      const filesWithPageCounts = await Promise.all(
        uploadedFiles.map(async (file) => {
          const pageCount = await getPageCountForFile(file);
          return {
            fileName: file.name,
            fileType:
              file.type === PDF_MIME_TYPE
                ? AppealSupportingDocumentFileType.Pdf
                : AppealSupportingDocumentFileType.Image,
            pageCount,
          };
        }),
      );

      const generateData = await generateUploadUrlsAndUploadFiles(
        uploadedFiles,
        filesWithPageCounts,
        selectedDocumentType,
        otherDocumentTypeName,
      );

      const fileIds = generateData.fileUploadDetails.map(
        (detail) => detail.fileId,
      );

      const upsertDocumentInput = {
        documentId: generateData.documentId,
        documentType: selectedDocumentType,
        otherDocumentTypeName,
        files: filesWithPageCounts.map((file, index) => ({
          ...file,
          fileId: fileIds[index],
        })),
      };

      if (document?.documentId) {
        await deps.deleteDocument(document.documentId);
      }

      await deps.upsertDocument(upsertDocumentInput);

      onSubmitDocument();
      closeModal();
    } catch (error) {
      const clientSafeMessage = deps.getClientSafeErrorMessage
        ? deps.getClientSafeErrorMessage(error)
        : null;
      setSaveDocumentError(clientSafeMessage ?? "Failed to upload, try again");
    } finally {
      setIsDocumentUpsertLoading(false);
    }
  };

  const handleFileView = (file: File) => {
    setCurrentView({
      view: DocumentUploadModalView.VIEW_FILE,
      file,
    });
  };

  const goBackToSelectDocumentType = () => {
    setUploadedFiles([]);
    setSaveDocumentError(null);

    if (mode === DocumentUploadModalMode.ADD) {
      setCurrentView({
        view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
      });
    } else {
      handleOnClose();
    }
  };

  const isAdditionalMode = (modeToCheck: DocumentUploadModalMode) =>
    additionalModes?.some((entry) => entry.mode === modeToCheck) ?? false;

  const isSkipFirstViewMode =
    mode === DocumentUploadModalMode.EDIT ||
    mode === DocumentUploadModalMode.ADD_LOMN ||
    mode === DocumentUploadModalMode.ADD_DENIAL_LETTER ||
    isAdditionalMode(mode);

  const handleUploadDocumentViewOnBack = () => {
    if (mode === DocumentUploadModalMode.ADD && hasUnsavedChanges) {
      setCurrentView({
        view: DocumentUploadModalView.CLOSE_CONFIRMATION,
        navigationDirection: DocumentUploadModalNavigationDirection.BACK,
      });
    } else if (isSkipFirstViewMode && hasUnsavedChanges) {
      setCurrentView({
        view: DocumentUploadModalView.CLOSE_CONFIRMATION,
        navigationDirection: DocumentUploadModalNavigationDirection.CLOSE,
      });
    } else if (mode === DocumentUploadModalMode.ADD) {
      goBackToSelectDocumentType();
    } else {
      closeModal();
    }
  };

  return (
    <Modal
      classNames={{ header: styles.modalHeader }}
      opened={isOpen}
      onClose={handleOnClose}
      title={
        [
          DocumentUploadModalView.DELETE_CONFIRMATION,
          DocumentUploadModalView.CLOSE_CONFIRMATION,
        ].includes(currentView.view) ? undefined : (
          <Title
            currentView={currentView.view}
            onNavigate={() => {
              if (!selectedDocumentType) {
                setCurrentView({
                  view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
                });
              } else {
                setCurrentView({
                  view: DocumentUploadModalView.UPLOAD_DOCUMENT,
                  documentType: selectedDocumentType,
                  otherDocumentName,
                });
              }
            }}
          />
        )
      }
      withCloseButton={
        [
          DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
          DocumentUploadModalView.UPLOAD_DOCUMENT,
        ].includes(currentView.view) && !isDocumentUpsertLoading
      }
      closeOnEscape={
        !isDocumentUpsertLoading &&
        currentView.view !== DocumentUploadModalView.CLOSE_CONFIRMATION
      }
      closeOnClickOutside={
        !isDocumentUpsertLoading &&
        currentView.view !== DocumentUploadModalView.CLOSE_CONFIRMATION
      }
      size={
        currentView.view === DocumentUploadModalView.VIEW_FILE ? "xl" : "md"
      }
    >
      {currentView.view === DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE && (
        <ChooseDocumentTypeView
          selectedDocumentType={selectedDocumentType}
          setSelectedDocumentType={setSelectedDocumentType}
          otherDocumentName={otherDocumentName}
          setOtherDocumentName={setOtherDocumentName}
          excludedDocumentTypes={excludedDocumentTypes}
          Button={Button}
          onContinue={() => {
            if (selectedDocumentType) {
              setCurrentView({
                view: DocumentUploadModalView.UPLOAD_DOCUMENT,
                documentType: selectedDocumentType,
                otherDocumentName,
              });
            }
          }}
        />
      )}
      {currentView.view === DocumentUploadModalView.UPLOAD_DOCUMENT &&
        selectedDocumentType && (
          <UploadDocumentView
            selectedDocumentType={selectedDocumentType}
            files={uploadedFiles}
            onBack={handleUploadDocumentViewOnBack}
            onFilesUploaded={(files: File[]) => {
              setSaveDocumentError(null);
              setUploadedFiles([...uploadedFiles, ...files]);
            }}
            onViewFile={handleFileView}
            onSaveDocument={handleSaveDocument}
            onDeleteFile={(file: File) => {
              setCurrentView({
                view: DocumentUploadModalView.DELETE_CONFIRMATION,
                file,
              });
            }}
            error={saveDocumentError ?? undefined}
            isSubmitLoading={isDocumentUpsertLoading}
            isLoadingInitialState={isLoadingDocumentFiles}
            mode={mode}
            documentTypeDescriptions={documentTypeDescriptions}
            Button={Button}
            UploadFileInput={UploadFileInput}
            additionalUploadContent={
              qrUpload ? (
                <QrCodeUploadSection
                  appealId={appealId}
                  documentType={selectedDocumentType}
                  onFilesUploaded={(files: File[]) => {
                    setSaveDocumentError(null);
                    setUploadedFiles((prev) => [...prev, ...files]);
                  }}
                  disabled={isDocumentUpsertLoading || isLoadingDocumentFiles}
                  phoneUploadPath={qrUpload.phoneUploadPath}
                  generateTemporaryUpload={qrUpload.generateTemporaryUpload}
                />
              ) : undefined
            }
          />
        )}
      {currentView.view === DocumentUploadModalView.VIEW_FILE && (
        <ViewFileView
          title={currentView.file.name}
          fileUrls={viewFileUrls ?? []}
          isLoading={isGettingViewFileUrls}
          hasError={hasErrorGettingViewFileUrls}
          size={currentView.file.size}
        />
      )}
      {currentView.view === DocumentUploadModalView.DELETE_CONFIRMATION && (
        <DeleteConfirmationView
          name={currentView.file.name}
          Button={Button}
          onCancel={() => {
            if (selectedDocumentType) {
              setCurrentView({
                view: DocumentUploadModalView.UPLOAD_DOCUMENT,
                documentType: selectedDocumentType,
                otherDocumentName,
              });
            } else {
              setCurrentView({
                view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
              });
            }
          }}
          onDelete={() => {
            setSaveDocumentError(null);
            setUploadedFiles(
              uploadedFiles.filter((file) => file !== currentView.file),
            );

            if (selectedDocumentType) {
              setCurrentView({
                view: DocumentUploadModalView.UPLOAD_DOCUMENT,
                documentType: selectedDocumentType,
                otherDocumentName,
              });
            } else {
              setCurrentView({
                view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
              });
            }
          }}
        />
      )}
      {currentView.view === DocumentUploadModalView.CLOSE_CONFIRMATION && (
        <CloseConfirmationView
          Button={Button}
          onCancel={() => {
            if (!selectedDocumentType) {
              setCurrentView({
                view: DocumentUploadModalView.CHOOSE_DOCUMENT_TYPE,
              });
            } else {
              setCurrentView({
                view: DocumentUploadModalView.UPLOAD_DOCUMENT,
                documentType: selectedDocumentType,
                otherDocumentName,
              });
            }
          }}
          onConfirm={
            currentView.navigationDirection ===
            DocumentUploadModalNavigationDirection.BACK
              ? goBackToSelectDocumentType
              : closeModal
          }
          mode={mode}
          navigationDirection={currentView.navigationDirection}
        />
      )}
    </Modal>
  );
};
